import React, { useState, useEffect, useCallback, useRef } from 'react';
import QRCode from 'qrcode';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import s from './CatalogoOnline.module.css';

const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '');
const CONFIG_PADRAO = { ativo: false, cor_primaria: '#1565ff', cor_secundaria: '#0f172a', mensagem_boas_vindas: '', banner_url: '', whatsapp: '' };

function normalizarSlug(v) {
  return v.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

export default function CatalogoOnline() {
  const { usuario } = useAuth();
  const orgId = usuario?.organizacao_id;

  const [carregando, setCarregando] = useState(true);
  const [slug, setSlug] = useState('');
  const [config, setConfig] = useState(CONFIG_PADRAO);
  const [bannerFile, setBannerFile] = useState(null);
  const [uploadandoBanner, setUploadandoBanner] = useState(false);
  const [checagemSlug, setChecagemSlug] = useState(null); // null | 'checando' | 'livre' | 'ocupado'
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const slugOriginal = useRef('');

  const [produtosCatalogo, setProdutosCatalogo] = useState([]);
  const [carregandoProdutosCatalogo, setCarregandoProdutosCatalogo] = useState(true);

  const setC = (campo, valor) => setConfig(prev => ({ ...prev, [campo]: valor }));

  useEffect(() => {
    if (!orgId) return;
    api.get(`/organizacoes/${orgId}`)
      .then(({ data }) => {
        setSlug(data.catalogo_slug || '');
        slugOriginal.current = data.catalogo_slug || '';
        setConfig({ ...CONFIG_PADRAO, ...(data.catalogo_config || {}) });
      })
      .catch(() => setErro('Erro ao carregar configurações do catálogo.'))
      .finally(() => setCarregando(false));
  }, [orgId]);

  // Checa disponibilidade do slug (debounce) — sempre que o valor muda e é
  // diferente do que já está salvo pra esta organização.
  useEffect(() => {
    if (!slug) { setChecagemSlug(null); return; }
    if (slug === slugOriginal.current) { setChecagemSlug('livre'); return; }
    setChecagemSlug('checando');
    const t = setTimeout(() => {
      api.get('/organizacoes/slug-disponivel', { params: { slug, id: orgId } })
        .then(({ data }) => setChecagemSlug(data.disponivel ? 'livre' : 'ocupado'))
        .catch(() => setChecagemSlug(null));
    }, 400);
    return () => clearTimeout(t);
  }, [slug, orgId]);

  const carregarProdutosCatalogo = useCallback(() => {
    if (!orgId) return;
    setCarregandoProdutosCatalogo(true);
    api.get(`/organizacoes/${orgId}/catalogo/produtos`)
      .then(({ data }) => setProdutosCatalogo(data))
      .catch(() => setProdutosCatalogo([]))
      .finally(() => setCarregandoProdutosCatalogo(false));
  }, [orgId]);

  useEffect(() => { carregarProdutosCatalogo(); }, [carregarProdutosCatalogo]);

  const alternarDestaque = async (produto) => {
    try {
      await api.patch(`/organizacoes/${orgId}/catalogo/produtos/${produto.produto_id}/destaque`, {
        destaque: !produto.destaque,
      });
      carregarProdutosCatalogo();
    } catch {
      setErro('Erro ao atualizar destaque do produto.');
    }
  };

  const moverProduto = async (produto, direcao) => {
    try {
      await api.post(`/organizacoes/${orgId}/catalogo/produtos/${produto.produto_id}/mover`, { direcao });
      carregarProdutosCatalogo();
    } catch {
      setErro('Erro ao reordenar produto.');
    }
  };

  const urlLoja = slug ? `${window.location.origin}/loja/${slug}` : '';

  useEffect(() => {
    if (!urlLoja) { setQrDataUrl(''); return; }
    QRCode.toDataURL(urlLoja, { width: 160, margin: 1 }).then(setQrDataUrl).catch(() => setQrDataUrl(''));
  }, [urlLoja]);

  const enviarBanner = useCallback(async () => {
    if (!bannerFile || !orgId) return;
    setErro(''); setSucesso(''); setUploadandoBanner(true);
    try {
      const form = new FormData();
      form.append('banner', bannerFile);
      const { data } = await api.post(`/organizacoes/${orgId}/catalogo-banner`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setC('banner_url', data.banner_url);
      setBannerFile(null);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao enviar banner.');
    } finally {
      setUploadandoBanner(false);
    }
  }, [bannerFile, orgId]);

  const salvar = async () => {
    if (config.ativo && !slug) {
      setErro('Informe o endereço do catálogo antes de ativar.');
      return;
    }
    if (checagemSlug === 'ocupado') {
      setErro('Esse endereço já está em uso por outra organização.');
      return;
    }
    setErro(''); setSucesso(''); setSalvando(true);
    try {
      await api.put(`/organizacoes/${orgId}/catalogo`, { catalogo_slug: slug || null, catalogo_config: config });
      slugOriginal.current = slug;
      setSucesso('Catálogo salvo com sucesso!');
      setTimeout(() => setSucesso(''), 3000);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar catálogo.');
    } finally {
      setSalvando(false);
    }
  };

  const bannerSrc = config.banner_url
    ? (config.banner_url.startsWith('http') ? config.banner_url : `${BACKEND_URL}${config.banner_url}`)
    : '';

  if (carregando) return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>🛍️</span> Catálogo Online</div>
      <div className={s.conteudo}>Carregando...</div>
    </div>
  );

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>🛍️</span> Catálogo Online</div>

      <div className={s.conteudo}>
        <div className={s.formGrid}>
          <div>
            <fieldset className={s.fieldset}>
              <legend className={s.legend}>Configurações Gerais</legend>

              <label className={s.checkRow}>
                <input type="checkbox" checked={!!config.ativo} onChange={e => setC('ativo', e.target.checked)} />
                Catálogo ativo (visível publicamente)
              </label>

              <label className={s.checkRow} title="Lanchonete, açaí e similares: o produto é feito na hora, sem estoque controlado">
                <input type="checkbox" checked={!!config.nao_verificar_estoque} onChange={e => setC('nao_verificar_estoque', e.target.checked)} />
                Não verificar estoque (nenhum produto aparece como "Esgotado")
              </label>

              <div className={s.campo} style={{ marginTop: 10 }}>
                <span className={s.campoLabel}>Endereço:</span>
                <span className={s.slugPrefixo}>{window.location.origin}/loja/</span>
                <input className={s.inputErp} value={slug}
                  onChange={e => setSlug(normalizarSlug(e.target.value))}
                  placeholder="minha-loja" />
                {checagemSlug === 'checando' && <span className={s.slugPrefixo}>checando...</span>}
                {checagemSlug === 'livre' && <span className={s.slugOk}>✔ disponível</span>}
                {checagemSlug === 'ocupado' && <span className={s.slugRuim}>✖ em uso</span>}
              </div>

              <div className={s.campo}>
                <span className={s.campoLabel}>Cor primária:</span>
                <input type="color" value={config.cor_primaria} onChange={e => setC('cor_primaria', e.target.value)} />
                <span className={s.campoLabel}>Cor secundária:</span>
                <input type="color" value={config.cor_secundaria} onChange={e => setC('cor_secundaria', e.target.value)} />
              </div>

              <div className={s.campo} style={{ alignItems: 'flex-start' }}>
                <span className={s.campoLabel}>Mensagem de boas-vindas:</span>
                <textarea className={s.textareaErp} value={config.mensagem_boas_vindas}
                  onChange={e => setC('mensagem_boas_vindas', e.target.value)}
                  placeholder="Bem-vindo à nossa loja!" />
              </div>

              <div className={s.campo}>
                <span className={s.campoLabel}>WhatsApp p/ pedidos:</span>
                <input className={s.inputErp} value={config.whatsapp}
                  onChange={e => setC('whatsapp', e.target.value)}
                  placeholder="(99) 99999-9999" />
              </div>
              <p className={s.ajudaCampo}>
                O cliente monta uma sacola na vitrine e o botão "Pedir no WhatsApp" abre uma
                conversa já com a lista de produtos — sem carrinho ou pagamento online, você
                fecha o pedido normalmente pelo WhatsApp. Deixe em branco pra esconder esse botão.
              </p>
            </fieldset>

            <fieldset className={s.fieldset}>
              <legend className={s.legend}>Banner</legend>
              {bannerSrc && <img src={bannerSrc} alt="Banner" style={{ maxHeight: 100, maxWidth: '100%', border: '1px solid var(--border)', borderRadius: 4, marginBottom: 8, display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />}
              <div className={s.campo}>
                <span className={s.campoLabel}>Arquivo:</span>
                <input type="file" accept="image/*" onChange={e => setBannerFile(e.target.files[0] || null)} />
              </div>
              <button className={s.btnErpPrimario} onClick={enviarBanner} disabled={uploadandoBanner || !bannerFile}>
                {uploadandoBanner ? 'Enviando...' : '⬆ Enviar Banner'}
              </button>
            </fieldset>
          </div>

          <div className={s.previewCard}>
            <div className={s.legend} style={{ textAlign: 'left' }}>Prévia</div>
            {urlLoja ? (
              <>
                <a className={s.previewLink} href={urlLoja} target="_blank" rel="noreferrer">{urlLoja}</a>
                {qrDataUrl && <img className={s.previewQr} src={qrDataUrl} width={160} height={160} alt="QR Code do catálogo" />}
              </>
            ) : (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Defina um endereço para gerar o link e o QR Code.</p>
            )}
          </div>
        </div>

        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Destaque e Ordenação</legend>
          <p className={s.ajudaCampo} style={{ marginBottom: 10 }}>
            Marque "Destaque" pra um produto sempre aparecer primeiro na vitrine (antes dos
            demais). Use as setas pra reordenar dentro do próprio grupo (destaque ou normal).
            Só lista produtos já marcados com o checkbox "Catálogo Online" no cadastro.
          </p>

          {carregandoProdutosCatalogo ? (
            <p className={s.ajudaCampo}>Carregando...</p>
          ) : produtosCatalogo.length === 0 ? (
            <p className={s.ajudaCampo}>
              Nenhum produto publicado ainda — marque "Catálogo Online" na aba Plataformas de
              Venda do cadastro de produtos.
            </p>
          ) : (
            <div className={s.listaProdutosCatalogo}>
              {produtosCatalogo.map((p, i) => {
                const grupoMesmoDestaque = produtosCatalogo.filter(x => x.destaque === p.destaque);
                const indiceGrupo = grupoMesmoDestaque.findIndex(x => x.produto_id === p.produto_id);
                const foto = p.foto_url
                  ? (p.foto_url.startsWith('http') ? p.foto_url : `${BACKEND_URL}${p.foto_url}`)
                  : null;
                return (
                  <div key={p.produto_id} className={s.linhaProdutoCatalogo}>
                    <div className={s.miniFoto}>
                      {foto ? <img src={foto} alt={p.nome} /> : <span>📦</span>}
                    </div>
                    <div className={s.linhaProdutoInfo}>
                      <span className={s.linhaProdutoNome}>{p.nome}</span>
                      {p.grupo_nome && <span className={s.linhaProdutoGrupo}>{p.grupo_nome}</span>}
                    </div>
                    <label className={s.checkRow}>
                      <input type="checkbox" checked={!!p.destaque} onChange={() => alternarDestaque(p)} />
                      Destaque
                    </label>
                    <div className={s.setasOrdem}>
                      <button className={s.btnErp} disabled={indiceGrupo === 0}
                        onClick={() => moverProduto(p, 'cima')}>▲</button>
                      <button className={s.btnErp} disabled={indiceGrupo === grupoMesmoDestaque.length - 1}
                        onClick={() => moverProduto(p, 'baixo')}>▼</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </fieldset>

        {erro    && <div className={s.erroMsg}>{erro}</div>}
        {sucesso && <div className={s.sucessoMsg}>{sucesso}</div>}
      </div>

      <div className={s.rodape}>
        <button className={s.btnErpPrimario} onClick={salvar} disabled={salvando} style={{ marginLeft: 'auto' }}>
          {salvando ? 'Salvando...' : '✔ Salvar'}
        </button>
      </div>
    </div>
  );
}
